import React, {
  useEffect,
  useState,
  useMemo,
  createContext,
  useContext,
  useRef,
  useCallback
} from 'react'
import injectSheet from 'react-jss'
import PageHead from '../../page-head/page-head'
import Button from 'pobble-components/dist/button'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchWorkUploadData,
  rotateMedium,
  addMedia,
  deleteMedia,
  deleteWork,
  saveWork,
  reorderMedia
} from '../../../stores/work-upload/work-upload.actions'
import { errorHandler } from 'pobble-components/dist/error-handler'
import { useRouteMatch, useHistory } from 'react-router-dom'
import classNames from 'classnames'
import {
  getMediaListInOrder,
  getWorkUploadState,
  getWork,
  getStudent
} from '../../../stores/work-upload/work-upload.selectors'
import {
  useMediaNav,
  MediaPageNav,
  MediaLoader
} from 'pobble-components/dist/work'
import { usePrevious } from 'pobble-components/dist/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUndo } from '@fortawesome/pro-solid-svg-icons/faUndo'
import { faTrashAlt } from '@fortawesome/pro-solid-svg-icons/faTrashAlt'
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faCaretLeft } from '@fortawesome/pro-solid-svg-icons/faCaretLeft'
import { faBringForward } from '@fortawesome/pro-solid-svg-icons/faBringForward'
import { faFilePlus } from '@fortawesome/pro-regular-svg-icons/faFilePlus'
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile'
import { openConfirmationDialog } from 'pobble-components/dist/modal/confirmation-dialog'
import FormFile from 'pobble-components/dist/form-elements/form-file'
import { ModalContent, Modal, ModalHeader } from 'pobble-components/dist/modal'
import duckRocket from './duck-rocket.gif'
import Cookies from 'js-cookie'
import Sortable from 'sortablejs'
import WorkForm from '../../work-form/work-form'
import { useWorkUpload } from '../../../hooks/work'
import Spinner from 'pobble-components/dist/spinner'

const WorkContext = createContext(null)

const reorderStyles = {
  head: {
    padding: '20px 0',
    display: 'flex',
    '@media (min-width: 575px)': {
      paddingTop: '30px'
    }
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '0 -10px',
    '& .work-reorder-ghost': {
      opacity: '0'
    }
  },
  cell: {
    width: '33.3333%',
    padding: '10px',
    '@media (min-width: 575px)': {
      width: '25%'
    }
  },
  media: {
    background: '#454545',
    paddingBottom: '133%',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    borderRadius: '8px',
    '&:hover': {
      cursor: 'grab'
    }
  }
}

const Reorder = injectSheet(reorderStyles)(({ classes }) => {
  const { media, setScreenState, onReorder } = useContext(WorkContext)
  const container = useRef(null)

  const [mediaCopy] = useState(media)

  useEffect(
    () => {
      new Sortable(container.current, {
        animation: 150,
        ghostClass: 'work-reorder-ghost',
        onEnd: e => {
          const ids = Array.from(e.to.children).map(item => item.dataset.id)
          onReorder(ids)
        }
      })
    },
    [onReorder]
  )

  return (
    <>
      <Holder>
        <div className={classes.head}>
          <DarkButton
            action={() => setScreenState('page')}
            tall
            icon={<FontAwesomeIcon icon={faCaretLeft} />}>
            Back
          </DarkButton>
        </div>
      </Holder>
      <Holder>
        <div className={classes.container} ref={container}>
          {mediaCopy.map(media => (
            <div className={classes.cell} data-id={media.id} key={media.id}>
              <div
                className={classes.media}
                style={{ backgroundImage: `url("${media.image_url}")` }}
              />
            </div>
          ))}
        </div>
      </Holder>
    </>
  )
})

const successModalStyles = {
  content: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    fontSize: '14px',
    lineHeight: '1.2',
    textAlign: 'center',
    minWidth: 'auto',
    maxWidth: 'calc(100vw - 40px)',
    width: '30em',
    padding: '2em',
    '& img': {
      margin: '0 0 2em',
      width: '12.5em',
      height: '12.5em'
    },
    '& h3': {
      fontSize: '1.57em',
      margin: '0 0 1em'
    },
    '& p': {
      margin: '0'
    },
    '& button': {
      marginBottom: '8px'
    }
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '24px',
    alignItems: 'center'
  },
  uploadingPending: {
    display: 'flex',
    alignItems: 'center',
    font: 'var(--body)',
    color: 'var(--gray)',
    '& > *': {
      font: 'var(--body-)',
      marginRight: '8px',
      color: 'var(--gray)'
    }
  }
}

const SuccessModal = injectSheet(successModalStyles)(
  ({ classes, data, ...props }) => {
    const history = useHistory()

    const { uploadWork, uploadingWork } = useWorkUpload()

    return (
      <Modal {...props}>
        {data && (
          <ModalContent legacy className={classes.content}>
            {uploadingWork ? (
              <p className={classes.uploadingPending}>
                <Spinner />
                Uploading work...
              </p>
            ) : (
              <>
                <img src={duckRocket} alt="Duck in the rocket" />
                <h3>And away they go</h3>
                {data.workState === 'published' && (
                  <p>
                    The work is now published on Pobble.
                    <br />
                    Your pupil will be so excited!
                  </p>
                )}
                {data.workState === 'evidence_bank' && (
                  <p>
                    The work is now saved in the
                    <br />
                    evidence bank.
                  </p>
                )}
                <div className={classes.buttons}>
                  <Button tall action={() => history.push('/')}>
                    Back to my actions
                  </Button>
                  <FormFile
                    multiple
                    accept="image/jpeg, image/png, .pdf"
                    onChange={files => {
                      Cookies.remove('prefillWorkDetails')
                      uploadWork(data.pupilId, files).then(() =>
                        props.requestClose()
                      )
                    }}>
                    <Button
                      className="metrics__add-more-work-for-pupil"
                      as="span"
                      icon={<FontAwesomeIcon icon={faFilePlus} />}
                      tall
                      simple>
                      Add another piece of work for {data.pupilName}
                    </Button>
                  </FormFile>
                  <Button
                    tall
                    simple
                    href={'/dashboard/pupil/' + data.pupilId + '/work'}
                    icon={<FontAwesomeIcon icon={faFile} />}>
                    Back to {data.pupilName}
                    's evidence bank
                  </Button>
                </div>
              </>
            )}
          </ModalContent>
        )}
      </Modal>
    )
  }
)

const DetailsModal = ({ classes, ...props }) => {
  return (
    <Modal closeButton {...props}>
      <ModalHeader>Save writing</ModalHeader>
      <DetailsForm {...props} />
    </Modal>
  )
}

const DetailsForm = ({ requestClose, onSubmit }) => {
  const work = useSelector(state => getWork(state))
  const { saveWorkRequest } = useSelector(state => getWorkUploadState(state))
  const mapProps = useMemo(
    () => {
      const cookie = Cookies.get('prefillWorkDetails')
      const defaultValues = !cookie
        ? { title: '', description: '' }
        : JSON.parse(cookie)
      const student = work.student_type
      return {
        pupilId: student.account.id,
        pupilName: student.account.first_name,
        canPublish: student.policy.can_publish,
        defaultValues,
        onSubmit: onSubmit
      }
    },
    [work, onSubmit]
  )
  return (
    <WorkForm
      {...mapProps}
      onCancel={requestClose}
      submitButton={
        <Button
          pending={saveWorkRequest.pending}
          type="submit"
          tall
          icon={<FontAwesomeIcon icon={faCheck} />}>
          Save writing
        </Button>
      }
    />
  )
}

const headStyles = {
  container: {
    justifyContent: 'flex-end'
  },
  doneButton: {
    minWidth: '100px',
    display: 'flex',
    justifyContent: 'center'
  }
}

const Head = injectSheet(headStyles)(({ classes, workVersionId, onDone }) => {
  return (
    <PageHead className={classes.container}>
      <Button action={onDone} tall className={classes.doneButton} center>
        Done
      </Button>
    </PageHead>
  )
})

const darkButtonStyles = ({ COLORS }) => ({
  button: {
    background: '#282828',
    borderColor: '#282828',
    transition: 'none',
    '& span': {
      display: 'none',
      '@media (min-width: 575px)': {
        display: 'inline'
      }
    },
    '& > div': {
      display: 'flex',
      justifyContent: 'center',
      marginRight: '0 !important',
      '@media (min-width: 575px)': {
        marginRight: '6px !important'
      }
    },
    '&:hover': {
      background: '#1f1f1f',
      borderColor: '#1f1f1f'
    },
    '&:active': {
      boxShadow: 'none',
      background: '#000',
      borderColor: '#000'
    }
  },
  active: {
    background: '#fff !important',
    color: '#282828 !important'
  }
})

const DarkButton = injectSheet(darkButtonStyles)(
  ({ classes, children, active, ...props }) => (
    <Button
      className={classNames(classes.button, { [classes.active]: active })}
      {...props}>
      <span>{children}</span>
    </Button>
  )
)

const holderStyles = {
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    padding: '0 20px'
  }
}

const Holder = injectSheet(holderStyles)(({ classes, className, ...props }) => (
  <div className={classNames(classes.container, className)} {...props} />
))

const pageControlsStyles = {
  container: {
    '& button': {
      marginRight: '1em'
    },
    '& > *:last-child': {
      marginRight: '0'
    }
  },
  longLabel: {
    display: 'none',
    '@media (min-width: 575px)': {
      display: 'inline'
    }
  }
}

const PageControls = injectSheet(pageControlsStyles)(
  ({ classes, currentMedia, workId, onDelete }) => {
    const dispatch = useDispatch()
    const { addMediaRequest, deleteWorkRequest } = useSelector(state =>
      getWorkUploadState(state)
    )
    const { setScreenState, screenState, media } = useContext(WorkContext)
    return (
      <div className={classes.container}>
        {media.length > 1 && (
          <DarkButton
            action={() => setScreenState('reorder')}
            tall
            icon={<FontAwesomeIcon icon={faBringForward} />}>
            Reorder
          </DarkButton>
        )}
        {screenState === 'page' && (
          <>
            <DarkButton
              pending={deleteWorkRequest.pending}
              action={() =>
                openConfirmationDialog({
                  bodyText: 'Are you sure you want to delete this page?',
                  action: onDelete
                })
              }
              tall
              icon={<FontAwesomeIcon icon={faTrashAlt} />}>
              Delete
              <span className={classes.longLabel}>&nbsp;page</span>
            </DarkButton>
            <FormFile
              disabled={addMediaRequest.pending}
              onChange={files => {
                dispatch(
                  addMedia(workId, {
                    image: files[0]
                  })
                ).catch(errorHandler)
              }}
              accept="image/jpeg, image/png, .pdf">
              <DarkButton
                pending={addMediaRequest.pending}
                span
                tall
                icon={<FontAwesomeIcon icon={faPlusCircle} />}>
                Add <span className={classes.longLabel}>&nbsp;page</span>
              </DarkButton>
            </FormFile>
          </>
        )}
      </div>
    )
  }
)

const editControlsStyles = {
  container: {
    marginRight: 'auto',
    '@media (min-width: 575px)': {
      marginRight: '1em'
    }
  }
}

const EditControls = injectSheet(editControlsStyles)(({ classes, media }) => {
  const dispatch = useDispatch()
  const { rotateMediumRequest } = useSelector(state =>
    getWorkUploadState(state)
  )
  return (
    <div className={classes.container}>
      <DarkButton
        pending={rotateMediumRequest.pending}
        action={() => dispatch(rotateMedium(media.id)).catch(errorHandler)}
        tall
        icon={<FontAwesomeIcon icon={faUndo} />}>
        Rotate
      </DarkButton>
    </div>
  )
})

const workStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#333',
    overflow: 'hidden'
  },
  imageHolder: {
    padding: '20px 20px 40px',
    '& > div': {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& img': {
      position: 'relative',
      zIndex: '10',
      width: '100%',
      background: '#000'
    }
  },
  controls: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px 20px 0',
    '@media (min-width: 575px)': {
      paddingTop: '30px'
    }
  },
  pageNavDesktop: {
    display: 'none',
    marginRight: 'auto',
    '@media (min-width: 575px)': {
      display: 'flex'
    }
  },
  pageNavMobile: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '-10px 0 30px',
    '@media (min-width: 575px)': {
      display: 'none'
    }
  }
}

const Work = ({ classes }) => {
  const match = useRouteMatch()
  const history = useHistory()
  const dispatch = useDispatch()
  const workId = match.params.id
  const media = useSelector(state => getMediaListInOrder(state))
  const student = useSelector(getStudent)
  const mediaNav = useMediaNav(media)
  const previousPagesLength = usePrevious(mediaNav.mediaLength)
  const [modalState, setModalState] = useState({
    type: null
  })
  const [screenState, setScreenState] = useState('page')

  const { params } = useRouteMatch()

  useEffect(
    () => {
      dispatch(fetchWorkUploadData(params.id)).catch(errorHandler)
    },
    [dispatch, params.id]
  )

  // Update page navigation when new media added
  useEffect(
    () => {
      if (mediaNav.mediaLength && previousPagesLength) {
        if (previousPagesLength < mediaNav.mediaLength) {
          mediaNav.setActiveMediaIndex(mediaNav.mediaLength - 1)
        }
        if (mediaNav.activeMediaIndex > mediaNav.mediaLength - 1) {
          mediaNav.setActiveMediaIndex(mediaNav.mediaLength - 1)
        }
      }
      if (!mediaNav.mediaLength && previousPagesLength > 1) {
        window.location.reload()
      }
    },
    [mediaNav.mediaLength] //eslint-disable-line
  )

  const onReorder = useCallback(
    ids => {
      dispatch(reorderMedia(workId, ids))
    },
    [dispatch, workId]
  )

  const value = useMemo(
    () => ({
      media,
      screenState,
      setScreenState,
      workId,
      onReorder
    }),
    [media, screenState, setScreenState, workId, onReorder]
  )

  return (
    <div className={classes.container}>
      <WorkContext.Provider value={value}>
        <Head onDone={() => setModalState({ type: 'Details' })} />
        {!!media.length && (
          <div>
            {screenState === 'page' && (
              <>
                <Holder className={classes.controls}>
                  <MediaPageNav
                    mediaNav={mediaNav}
                    className={classes.pageNavDesktop}
                  />
                  <EditControls media={mediaNav.activeMedia} />
                  <PageControls
                    workId={workId}
                    onDelete={() => {
                      if (media.length > 1) {
                        dispatch(deleteMedia(mediaNav.activeMedia.id)).catch(
                          errorHandler
                        )
                      } else {
                        dispatch(deleteWork(workId))
                          .then(() => history.push('/'))
                          .catch(errorHandler)
                      }
                    }}
                    currentMedia={mediaNav.activeMedia}
                  />
                </Holder>
                <Holder className={classes.imageHolder}>
                  {mediaNav.activeMedia && (
                    <MediaLoader media={mediaNav.activeMedia} />
                  )}
                </Holder>
                <Holder className={classes.pageNavMobile}>
                  <MediaPageNav mediaNav={mediaNav} />
                </Holder>
              </>
            )}
            {screenState === 'reorder' && <Reorder />}
          </div>
        )}
      </WorkContext.Provider>
      <DetailsModal
        requestClose={() => setModalState({ type: null })}
        onSubmit={({ title, description, workState, tags, comment, sta }) => {
          Cookies.set(
            'prefillWorkDetails',
            JSON.stringify({
              title,
              description
            }),
            { expires: 1 }
          )
          const payload = {
            title,
            description,
            state: workState,
            comment_on_work: comment,
            tags
          }
          if (sta) payload.independent = sta.value
          if (sta && sta.extraInfo)
            payload.independent_description = sta.extraInfo

          return dispatch(saveWork(workId, payload))
            .catch(errorHandler)
            .then(() =>
              setModalState({
                type: 'Success',
                data: {
                  ...modalState.data,
                  workState,
                  pupilName: student.account.first_name,
                  pupilId: student.account.id
                }
              })
            )
        }}
        isOpen={modalState.type === 'Details'}
      />
      <SuccessModal
        requestClose={() => setModalState({ type: null })}
        isOpen={modalState.type === 'Success'}
        data={modalState.data}
      />
    </div>
  )
}

export default injectSheet(workStyles)(Work)
